import {
  Box,
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  NegotiationRoundStatus,
  useAddNegotiationRoundV2InviteMutation,
  useGetOrganizationQuery,
  useRemoveNegotiationRoundV2InviteMutation,
  useUpdateNegotiationRoundV2InviteMessageMutation,
  type NegotiationRoundV2Dto,
  type SourcingEventV2Dto,
} from "../../../../../autogen/bff-api";
import { useApiError } from "../../../../../common/errors/useApiError";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { useConfirmationModal } from "../../../../../common/useConfirmationModal";
import { roundInviteCandidates, type RoundInviteCandidate } from "../../rounds/roundInviteCandidates";

interface Props {
  event: SourcingEventV2Dto;
  round: NegotiationRoundV2Dto;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

interface MessageDraft {
  organizationId: string;
  personIds: string[];
  message: string;
  isNew: boolean;
}

export const RoundInvitationsStep = ({ event, round, previousStep, nextStep, nextStepLabel }: Props) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const confirmationModal = useConfirmationModal();

  const [addInvite] = useAddNegotiationRoundV2InviteMutation();
  const [removeInvite] = useRemoveNegotiationRoundV2InviteMutation();
  const [updateInviteMessage] = useUpdateNegotiationRoundV2InviteMessageMutation();

  const [messageDraft, setMessageDraft] = useState<MessageDraft | null>(null);

  const isPublished = round.status === NegotiationRoundStatus.Published;
  const canEditMessages = round.status === NegotiationRoundStatus.Draft;
  const roundInviteFor = (organizationId: string) =>
    round.invites.find((invite) => invite.organizationId === organizationId);

  const performAdd = async (draft: MessageDraft) => {
    try {
      await addInvite({
        eventId: event.id,
        roundId: round.id,
        addNegotiationRoundV2InviteRequest: {
          organizationId: draft.organizationId,
          personIds: draft.personIds,
          message: draft.message || null,
        },
      }).unwrap();
      setMessageDraft(null);
    } catch (e) {
      displayer.trigger(e);
    }
  };

  const performMessageUpdate = async (draft: MessageDraft) => {
    try {
      await updateInviteMessage({
        eventId: event.id,
        roundId: round.id,
        pathOrganizationId: draft.organizationId,
        updateNegotiationRoundV2InviteMessageRequest: { message: draft.message || null },
      }).unwrap();
      setMessageDraft(null);
    } catch (e) {
      displayer.trigger(e);
    }
  };

  const performRemove = async (organizationId: string) => {
    try {
      await removeInvite({
        eventId: event.id,
        roundId: round.id,
        pathOrganizationId: organizationId,
      }).unwrap();
    } catch (e) {
      displayer.trigger(e);
    }
  };

  const saveDraft = (draft: MessageDraft) => {
    if (draft.isNew && isPublished) {
      confirmationModal.show({
        title: t("Confirm invite"),
        description: t("You are live, and the selected organization will immediately be invited to the event"),
        onConfirmed: () => void performAdd(draft),
        confirmButtonColor: "blue",
        confirmButtonText: t("Create") ?? "",
      });
    } else if (draft.isNew) {
      void performAdd(draft);
    } else {
      void performMessageUpdate(draft);
    }
  };

  const removeWithConfirmation = (organizationId: string) => {
    if (isPublished) {
      confirmationModal.show({
        title: t("Confirm removal"),
        description: t("You are live, and the selected organizations wil immediately be removed from the event"),
        onConfirmed: () => void performRemove(organizationId),
        confirmButtonColor: "blue",
        confirmButtonText: t("Send") ?? "",
      });
    } else {
      void performRemove(organizationId);
    }
  };

  const candidates = roundInviteCandidates(event, round);

  const openDraftFor = (candidate: RoundInviteCandidate) => {
    const existing = roundInviteFor(candidate.organizationId);
    setMessageDraft({
      organizationId: candidate.organizationId,
      personIds: candidate.personIds,
      message: existing?.message ?? "",
      isNew: !existing,
    });
  };

  return (
    <Box pb="8" pt="10">
      <Heading as="h2" size="md" mb="6">
        {t("Invitations")}
      </Heading>
      <Text fontSize="sm" color="smMuted" pb="4">
        {t("Choose which of the event's organizations are invited to this round.")}
      </Text>

      {confirmationModal.modal}

      <Modal isOpen={!!messageDraft} onClose={() => setMessageDraft(null)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{messageDraft?.isNew ? t("Add Invitation Message") : t("Edit Invitation Message")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {messageDraft && (
              <>
                <Text mb="2">
                  {t("Message to")}: <OrgName organizationId={messageDraft.organizationId} />
                </Text>
                <Textarea
                  value={messageDraft.message}
                  onChange={(e) => setMessageDraft({ ...messageDraft, message: e.target.value })}
                  placeholder={`${t("Enter message for the organization")}`}
                  rows={5}
                />
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={() => setMessageDraft(null)}>
              {t("Cancel")}
            </Button>
            <Button colorScheme="blue" onClick={() => messageDraft && saveDraft(messageDraft)}>
              {messageDraft?.isNew ? t("Create invite") : t("Save Message")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Box border="1px solid" borderColor="smBorder" rounded="md" overflow="hidden">
        <TableContainer>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>{t("Organization name")}</Th>
                <Th>{t("Recipients")}</Th>
                <Th>{t("Message")}</Th>
                <Th textAlign="center">{t("Invited")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {candidates.length === 0 && (
                <Tr>
                  <Td colSpan={4} textAlign="center" color="smMuted">
                    {t("No invites have been added")}
                  </Td>
                </Tr>
              )}
              {candidates.map((candidate) => (
                <InviteRow
                  key={candidate.organizationId}
                  candidate={candidate}
                  roundInvite={roundInviteFor(candidate.organizationId)}
                  canEditMessage={canEditMessages}
                  onOpenMessage={() => openDraftFor(candidate)}
                  onRemove={() => removeWithConfirmation(candidate.organizationId)}
                />
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
    </Box>
  );
};

const InviteRow = ({
  candidate,
  roundInvite,
  canEditMessage,
  onOpenMessage,
  onRemove,
}: {
  candidate: RoundInviteCandidate;
  roundInvite: NegotiationRoundV2Dto["invites"][number] | undefined;
  canEditMessage: boolean;
  onOpenMessage: () => void;
  onRemove: () => void;
}) => {
  const { t } = useTranslation();
  const isInvited = !!roundInvite;
  const message = roundInvite?.message ?? "";

  const recipients = isInvited
    ? [
        ...roundInvite.persons.map((person) => person.name),
        ...(roundInvite.emailInvites ?? []).map((emailInvite) => emailInvite.email),
      ]
    : candidate.recipientLabels;

  return (
    <Tr>
      <Td verticalAlign="top" fontWeight="medium">
        <OrgName organizationId={candidate.organizationId} />
      </Td>
      <Td verticalAlign="top">
        {recipients.length > 0 ? (
          recipients.map((recipient) => (
            <Text key={recipient} fontSize="sm">
              {recipient}
            </Text>
          ))
        ) : (
          <Text fontSize="sm" color="smMuted">
            –
          </Text>
        )}
      </Td>
      <Td verticalAlign="top">
        {isInvited && canEditMessage ? (
          <Button size="sm" variant="ghost" color={message ? "smText" : "smMuted"} onClick={onOpenMessage}>
            {message ? (message.length > 30 ? `${message.substring(0, 30)}...` : message) : t("Add message")}
          </Button>
        ) : isInvited && message ? (
          <Text fontSize="sm" whiteSpace="pre-wrap">
            {message}
          </Text>
        ) : (
          <Text fontSize="sm" color="smMuted">
            –
          </Text>
        )}
      </Td>
      <Td verticalAlign="top" textAlign="center">
        <Switch
          aria-label="toggle invite"
          isChecked={isInvited}
          colorScheme="teal"
          onChange={(e) => {
            if (e.target.checked) onOpenMessage();
            else onRemove();
          }}
        />
      </Td>
    </Tr>
  );
};

const OrgName = ({ organizationId }: { organizationId: string }) => {
  const { data: org } = useGetOrganizationQuery({ orgId: organizationId });
  return <>{org?.name ?? "…"}</>;
};
